import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const EMPTY = {
  customer_id: "", category: "", provider: "", provider_contract_number: "", order_number: "",
  tariff_name: "", start_date: "", end_date: "", term_months: 24, cancellation_notice_days: 90,
  cancellation_deadline: "", deadline_verified: false, monthly_fee: 0, one_time_charges: 0,
  discount: "", status: "aktiv", notes: "",
};

export function ContractForm({ open, onOpenChange, contract, fixedCustomerId, onSaved, registry }) {
  const { tenant } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!contract;

  const { data: custData } = useQuery({
    queryKey: ["customers-min"],
    queryFn: async () => (await api.get("/customers?limit=200")).data,
    enabled: open && !fixedCustomerId,
  });

  const enabledModules = tenant?.modules || [];
  const categories = (registry?.modules || [])
    .filter((m) => enabledModules.includes(m.id) && m.status === "available")
    .flatMap((m) => m.contract_categories);

  useEffect(() => {
    if (open) {
      const base = contract ? { ...EMPTY, ...contract } : { ...EMPTY };
      if (fixedCustomerId) base.customer_id = fixedCustomerId;
      setForm(base);
    }
  }, [open, contract, fixedCustomerId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Bitte einen Kunden wählen");
    if (!form.category) return toast.error("Bitte eine Kategorie wählen");
    setSaving(true);
    try {
      const payload = { ...form, term_months: Number(form.term_months) || 0,
        cancellation_notice_days: Number(form.cancellation_notice_days) || 0,
        monthly_fee: Number(form.monthly_fee) || 0, one_time_charges: Number(form.one_time_charges) || 0 };
      if (isEdit) await api.put(`/contracts/${contract.id}`, payload);
      else await api.post("/contracts", payload);
      toast.success(isEdit ? "Vertrag aktualisiert" : "Vertrag angelegt");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? "Vertrag bearbeiten" : "Neuer Vertrag"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!fixedCustomerId && (
            <div className="space-y-1.5"><Label>Kunde</Label>
              <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                <SelectTrigger data-testid="contract-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>
                  {(custData?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name} ({c.customer_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="contract-category-select"><SelectValue placeholder="Kategorie" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Anbieter</Label><Input value={form.provider} onChange={(e) => set("provider", e.target.value)} data-testid="contract-provider-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Tarifname</Label><Input value={form.tariff_name} onChange={(e) => set("tariff_name", e.target.value)} data-testid="contract-tariff-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Anbieter-Vertragsnr.</Label><Input value={form.provider_contract_number} onChange={(e) => set("provider_contract_number", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Auftragsnummer</Label><Input value={form.order_number} onChange={(e) => set("order_number", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Startdatum</Label><Input type="date" value={form.start_date?.slice(0,10) || ""} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Enddatum</Label><Input type="date" value={form.end_date?.slice(0,10) || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="contract-end-date-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Laufzeit (Monate)</Label><Input type="number" value={form.term_months} onChange={(e) => set("term_months", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Kündigungsfrist (Tage)</Label><Input type="number" value={form.cancellation_notice_days} onChange={(e) => set("cancellation_notice_days", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Monatl. Gebühr (€)</Label><Input type="number" step="0.01" value={form.monthly_fee} onChange={(e) => set("monthly_fee", e.target.value)} data-testid="contract-fee-input" /></div>
            <div className="space-y-1.5"><Label>Einmalkosten (€)</Label><Input type="number" step="0.01" value={form.one_time_charges} onChange={(e) => set("one_time_charges", e.target.value)} /></div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <div><span className="text-sm">Kündigungsfrist geprüft</span><p className="text-[11px] text-muted-foreground">Bestätigt durch Mitarbeiter</p></div>
            <Switch checked={form.deadline_verified} onCheckedChange={(v) => set("deadline_verified", v)} data-testid="deadline-verified-switch" />
          </div>
          <div className="space-y-1.5"><Label>Notizen</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} data-testid="contract-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
